import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ShieldCheck,
  Bot,
  Radio,
  Activity,
  ShieldAlert,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Mail,
  PauseCircle,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

function pct(num, denom) {
  if (!denom || denom <= 0) return 0;
  return Math.round((num / denom) * 100);
}

function KpiCard({ icon: Icon, label, value, subtext, accent = 'primary', testid }) {
  const accentMap = {
    primary: 'text-primary',
    success: 'text-green-400',
    warning: 'text-yellow-400',
    danger: 'text-red-400',
    muted: 'text-muted-foreground',
  };
  return (
    <Card
      className="p-4 border-border/40 bg-card/80 backdrop-blur-sm hover:border-primary/30 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
          {label}
        </p>
        <Icon className={`w-4 h-4 ${accentMap[accent]}`} />
      </div>
      <p className={`text-3xl font-black font-mono tracking-tight ${accentMap[accent]}`}>
        {value}
      </p>
      {subtext && (
        <p className="text-[11px] text-muted-foreground mt-1">{subtext}</p>
      )}
    </Card>
  );
}

function FunnelStage({ name, count, prevCount, isLast }) {
  const conv = prevCount != null && prevCount > 0
    ? Math.round((count / prevCount) * 100)
    : null;
  // Width % relative to the first stage
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground">{name}</span>
        <span className="font-mono text-muted-foreground">
          <span className="text-foreground font-bold">{count}</span>
          {conv != null && (
            <span className="ml-2 text-[10px] text-muted-foreground">
              · {conv}% conv.
            </span>
          )}
        </span>
      </div>
      <div className="h-2 rounded-full bg-secondary/40 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary/80 to-primary/40"
          style={{ width: `${conv != null ? Math.max(conv, 4) : 100}%` }}
        />
      </div>
      {!isLast && (
        <div className="flex justify-center text-muted-foreground/50 text-[10px] py-0.5">↓</div>
      )}
    </div>
  );
}

const KIND_META = {
  new_signup: { icon: Users, color: 'text-primary', tag: 'SIGNUP' },
  email_verified: { icon: Mail, color: 'text-green-400', tag: 'VERIFY' },
  trade_executed: { icon: TrendingUp, color: 'text-blue-300', tag: 'TRADE' },
  risk_pause: { icon: PauseCircle, color: 'text-yellow-400', tag: 'PAUSE' },
};

function ActivityFeed({ events }) {
  if (!events?.length) {
    return (
      <p className="text-xs text-muted-foreground py-6 text-center">
        No recent activity.
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="admin-activity-list">
      {events.map((e, idx) => {
        const meta = KIND_META[e.kind] || {
          icon: Activity,
          color: 'text-muted-foreground',
          tag: e.kind?.toUpperCase() || 'EVENT',
        };
        const Icon = meta.icon;
        let when = '';
        try {
          when = e.at ? formatDistanceToNow(new Date(e.at), { addSuffix: true }) : '';
        } catch {
          /* noop */
        }
        return (
          <li
            key={`${e.kind}-${idx}-${e.at}`}
            className="flex items-start gap-3 px-3 py-2 rounded-md border border-border/30 bg-secondary/10 hover:bg-secondary/20 transition-colors"
            data-testid={`activity-event-${idx}`}
          >
            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded border border-current/30 ${meta.color}`}>
                  {meta.tag}
                </span>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {when}
                </span>
              </div>
              <p className="text-xs text-foreground mt-0.5 truncate" title={e.label}>
                {e.label}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function AdminDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [activity, setActivity] = useState(null);
  const [perSymbol, setPerSymbol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    if (!token || !isAdmin) return;
    setRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, f, a, ps] = await Promise.all([
        fetch(`${API_URL}/api/admin/insights/operator`, { headers }),
        fetch(`${API_URL}/api/admin/insights/onboarding-stages`, { headers }),
        fetch(`${API_URL}/api/admin/insights/recent-activity?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/insights/per-symbol?limit=5`, { headers }),
      ]);
      if (s.ok) setSummary(await s.json());
      if (f.ok) setFunnel(await f.json());
      if (a.ok) setActivity(await a.json());
      if (ps.ok) setPerSymbol(await ps.json());
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('admin load:', e);
      setError('Could not load admin metrics. Retrying in 30s.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [isAdmin, load]);

  // Non-admins → 403 panel
  if (user && !isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card
          className="p-8 max-w-md text-center bg-card border-border/40"
          data-testid="admin-403"
        >
          <ShieldAlert className="w-10 h-10 mx-auto text-yellow-400 mb-3" />
          <h2 className="text-lg font-bold mb-1">Operator console</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This area is restricted to platform administrators.
          </p>
          <Button onClick={() => navigate('/paper-trading')} variant="outline" size="sm">
            Back to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (!user || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const u = summary?.users || {};
  const auto = summary?.automation || {};
  const tr = summary?.trades || {};
  const sg = summary?.signals || {};
  const rp = summary?.risk_pauses || {};
  const stages = funnel?.stages || [];
  const totalActivePauses =
    (rp.loss_streak || 0) + (rp.daily_drawdown || 0) + (rp.emergency_stop || 0);

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
              Operator Console
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Read-only platform overview · auto-refresh every 30s
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {lastRefresh && (
              <span className="font-mono">
                Updated {formatDistanceToNow(lastRefresh, { addSuffix: true })}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={refreshing}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded border border-yellow-500/40 bg-yellow-500/10 text-xs text-yellow-200">
            {error}
          </div>
        )}

        {/* KPI row */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6"
          data-testid="admin-kpi-row"
        >
          <KpiCard
            icon={Users}
            label="Total Users"
            value={u.total ?? '–'}
            subtext={`+${u.signups_24h ?? 0} in last 24h`}
            accent="primary"
            testid="kpi-total-users"
          />
          <KpiCard
            icon={ShieldCheck}
            label="Verified"
            value={`${pct(u.verified, u.total)}%`}
            subtext={`${u.verified ?? 0} of ${u.total ?? 0}`}
            accent={pct(u.verified, u.total) >= 50 ? 'success' : 'warning'}
            testid="kpi-verified"
          />
          <KpiCard
            icon={Bot}
            label="Automation On"
            value={`${pct(auto.enabled, auto.configs)}%`}
            subtext={`${auto.enabled ?? 0} active sandboxes`}
            accent="success"
            testid="kpi-automation"
          />
          <KpiCard
            icon={Radio}
            label="Signals 24h"
            value={sg.last_24h ?? 0}
            subtext={`${sg.total ?? 0} all-time`}
            accent="primary"
            testid="kpi-signals-24h"
          />
          <KpiCard
            icon={TrendingUp}
            label="Trades Open"
            value={tr.open ?? 0}
            subtext={`${tr.total ?? 0} total · ${tr.win_rate ?? 0}% wins`}
            accent="muted"
            testid="kpi-trades-open"
          />
          <KpiCard
            icon={PauseCircle}
            label="Paused by Risk"
            value={totalActivePauses}
            subtext={`${rp.loss_streak ?? 0} streak · ${rp.daily_drawdown ?? 0} DD`}
            accent={totalActivePauses > 0 ? 'warning' : 'muted'}
            testid="kpi-risk-paused"
          />
        </div>

        {/* 2-column section: Funnel + Risk Engine */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="p-5 border-border/40 bg-card" data-testid="admin-funnel-card">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-bold uppercase tracking-wider">Onboarding Funnel</h2>
            </div>
            <div className="space-y-3">
              {stages.map((s, i) => (
                <FunnelStage
                  key={s.name}
                  name={s.name}
                  count={s.count}
                  prevCount={i === 0 ? null : stages[0].count}
                  isLast={i === stages.length - 1}
                />
              ))}
            </div>
          </Card>

          <Card className="p-5 border-border/40 bg-card" data-testid="admin-risk-card">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert
                className={`w-4 h-4 ${totalActivePauses > 0 ? 'text-yellow-400' : 'text-muted-foreground'}`}
              />
              <h2 className="text-sm font-bold uppercase tracking-wider">Risk Engine Status</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Loss-streak pauses</span>
                <span className={`font-mono font-bold ${rp.loss_streak ? 'text-yellow-300' : 'text-foreground'}`}>
                  {rp.loss_streak ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Daily-drawdown pauses</span>
                <span className={`font-mono font-bold ${rp.daily_drawdown ? 'text-yellow-300' : 'text-foreground'}`}>
                  {rp.daily_drawdown ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Emergency stops</span>
                <span className={`font-mono font-bold ${rp.emergency_stop ? 'text-red-400' : 'text-foreground'}`}>
                  {rp.emergency_stop ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Market quality filter</span>
                <span
                  className={`font-mono font-bold ${
                    rp.market_quality_blocked ? 'text-yellow-300' : 'text-green-400'
                  }`}
                  data-testid="risk-market-quality"
                >
                  {rp.market_quality_blocked ? 'WEAK · blocking fan-out' : 'Healthy'}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Custom-source users</span>
                <span className="font-mono font-bold text-foreground">{auto.custom_source ?? 0}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded border border-border/30 bg-secondary/10">
                <span className="text-muted-foreground">Platform total P&L</span>
                <span
                  className={`font-mono font-bold ${
                    (tr.total_pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {(tr.total_pnl ?? 0) >= 0 ? '+' : ''}${(tr.total_pnl ?? 0).toFixed(2)}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
              Counts reflect users currently paused. Healthy state = zeros across all three pause
              types. Read-only — manual resume happens from the user's own Paper Trading page.
            </p>
          </Card>
        </div>

        {/* Market Performance — top symbols by realized P&L */}
        <Card
          className="p-5 border-border/40 bg-card mb-6"
          data-testid="admin-market-card"
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-bold uppercase tracking-wider">Market Performance</h2>
            </div>
            <span className="text-[10px] text-muted-foreground font-mono">
              top {perSymbol?.symbols?.length ?? 0} · closed real trades
            </span>
          </div>
          {!perSymbol?.symbols?.length ? (
            <p
              className="text-xs text-muted-foreground py-6 text-center"
              data-testid="admin-market-empty"
            >
              No closed trades yet — symbols will appear once the platform
              starts executing real fills.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="admin-market-list">
              {perSymbol.symbols.map((row) => {
                const positive = row.total_pnl >= 0;
                return (
                  <li
                    key={row.symbol}
                    className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-2 rounded-md border border-border/30 bg-secondary/10 hover:bg-secondary/20 transition-colors"
                    data-testid={`market-row-${row.symbol}`}
                  >
                    <div className="min-w-0">
                      <p className="font-bold font-mono text-sm text-foreground truncate">
                        {row.symbol}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {row.total_closed_trades} trade
                        {row.total_closed_trades === 1 ? '' : 's'}
                        {' · '}
                        {row.wins}W / {row.losses}L
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-mono font-black tabular-nums text-sm sm:text-base ${
                          positive ? 'text-green-400' : 'text-red-400'
                        }`}
                        data-testid={`market-pnl-${row.symbol}`}
                      >
                        {positive ? '+' : ''}${row.total_pnl.toFixed(2)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        avg {positive ? '+' : ''}${row.avg_pnl_per_trade.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right min-w-[64px]">
                      <p
                        className={`font-mono font-bold text-xs sm:text-sm ${
                          row.win_rate >= 50 ? 'text-green-400' : 'text-yellow-300'
                        }`}
                      >
                        {row.win_rate}%
                      </p>
                      <p className="text-[10px] text-muted-foreground">WR</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Activity feed */}
        <Card className="p-5 border-border/40 bg-card" data-testid="admin-activity-card">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-bold uppercase tracking-wider">Recent Activity</h2>
            </div>
            <span className="text-[10px] text-muted-foreground font-mono">
              last {activity?.events?.length ?? 0}
            </span>
          </div>
          <ActivityFeed events={activity?.events || []} />
        </Card>
      </div>
    </div>
  );
}
